import { offlineMockTransport } from "./mock-api.js";
import { isValidDate } from "./matrix.js";

// HTTP API adapter. Dictionaries are independent requests, never product-derived.
// Standalone demo.html intentionally uses the explicit in-memory mock backend.
// Normal HTTP(S) always fetches; errors are reported rather than silently mocked.
export class ApiError extends Error {
    constructor(message, status = 0) {
        super(message);
        this.name = "ApiError";
        this.status = status;
    }
}

const text = (value) => typeof value === "string" && value.length > 0;
const numeric = (value) => typeof value === "number" && Number.isFinite(value);
const validId = (value) =>
    text(value) || (Number.isSafeInteger(value) && value > 0);
const uniqueIds = (items) =>
    new Set(items.map((item) => String(item.id))).size === items.length;
const optionalText = (value) => value == null || text(value);
const optionalSort = (value) => value == null || Number.isFinite(value);
const normalizeProduct = (product) => ({
    ...product,
    id: String(product.id),
    brandId: String(product.brandId),
    countryId: String(product.countryId),
});
const normalizeDictionary = (rows) =>
    rows
        .map((row) => ({ ...row, id: String(row.id) }))
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
const brandSchema = (value) =>
    value &&
    validId(value.id) &&
    text(value.name) &&
    optionalText(value.code) &&
    optionalText(value.color) &&
    optionalText(value.logo_url)
const countrySchema = (value) =>
    value &&
    validId(value.id) &&
    text(value.name) &&
    optionalText(value.code)
const productSchema = (value) =>
    value &&
    [value.id, value.brandId, value.countryId].every(validId) &&
    [value.name].every(text) &&
    numeric(value.price) &&
    value.price >= 0 &&
    (value.currency === undefined || text(value.currency)) &&
    // (value.ram === undefined || (numeric(value.ram) && value.ram > 0)) &&
    // (value.storage === undefined ||
    //     (numeric(value.storage) && value.storage > 0)) &&
    (value.launchDate === undefined ||
        value.launchDate === "" ||
        isValidDate(value.launchDate)) &&
    (value.configurations === undefined ||
        (Array.isArray(value.configurations) &&
            value.configurations.every(
                (pair) =>
                    Array.isArray(pair) &&
                    pair.length === 2 &&
                    pair.every((n) => numeric(n) && n > 0),
            )));
const arraySchema = (check) => (value) =>
    Array.isArray(value) && value.every(check) && uniqueIds(value);
const productsSchema = (value) =>
    value &&
    arraySchema(productSchema)(value.items) &&
    Number.isInteger(value.total) &&
    value.total === value.items.length;

async function request(path, { signal, ...options } = {}, schema) {
    signal?.throwIfAborted();
    const transport =
        globalThis.location?.protocol === "file:"
            ? offlineMockTransport
            : globalThis.fetch;
    if (typeof transport !== "function")
        throw new ApiError("Fetch transport is unavailable");
    const response = await transport(path, {
        ...options,
        signal,
        headers: { Accept: "application/json", ...options.headers },
    });
    signal?.throwIfAborted();
    let data;
    try {
        data = await response.json();
    } catch (error) {
        signal?.throwIfAborted();
        throw new ApiError(
            `API returned invalid JSON (${response.status})`,
            response.status,
        );
    }
    signal?.throwIfAborted();
    if (!response.ok)
        throw new ApiError(
            typeof data?.message === "string"
                ? data.message
                : `API request failed (${response.status})`,
            response.status,
        );
    data = data.data;
    if (!data || (schema && !schema(data)))
        throw new ApiError("API response schema is invalid", response.status);
    return data;
}

export function fetchBrands({ signal } = {}) {
    return request("/api/brands", { signal }, arraySchema(brandSchema)).then(
        normalizeDictionary,
    );
}

export function fetchCountries({ signal } = {}) {
    return request("/api/countries", { signal }, arraySchema(countrySchema)).then(
        normalizeDictionary,
    );
}

export function fetchProductFilterOptions({ signal } = {}) {
    return request("/api/product-filter-options", { signal });
}

// Query contract: query, brandIds[], countryId, ram, storage, network,
// launchStart/launchEnd (YYYY-MM-DD), minPrice/maxPrice. Empty brandIds = NONE.
// Inclusive date/price bounds. Backend returns all matches ordered by price DESC.
export function fetchProducts(query = {}, { signal } = {}) {
    const params = new URLSearchParams();

    if (query.brandIds && Array.isArray(query.brandIds) && query.brandIds.length > 0) {
        params.append("brandIds", query.brandIds.join(","))
    } else {
        // throw new TypeError("brandIds must be an array of non-empty IDs");
    }

    for (const key of [
        "query",
        "countryId",
        "ram",
        "storage",
        "network",
        "launchStart",
        "launchEnd",
        "groupOfRamRom"
        // "minPrice",
        // "maxPrice",
    ]) {
        if (query[key] !== undefined && query[key] !== null)
            params.set(key, String(query[key]));
    }
    return request(`/api/products?${params}`, { signal }, productsSchema).then(
        (result) => ({ ...result, items: result.items.map(normalizeProduct) }),
    );
}

export function updateProductPrice(id, price, { signal } = {}) {
    if (!validId(id)) throw new TypeError("Product ID is required");
    return request(
        `/api/products/${encodeURIComponent(id)}/price`,
        {
            method: "PATCH",
            signal,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ price }),
        },
        productSchema,
    ).then(normalizeProduct);
}
